const TERMINAL_STATUSES = new Set(["succeeded", "failed"]);

export function isTerminalApplicationCreationOperation(operation) {
  return TERMINAL_STATUSES.has(operation && operation.status);
}

export function latestApplicationCreationMessage(operation) {
  const messages = operation && operation.messages;
  if (!Array.isArray(messages) || messages.length === 0) {
    return "";
  }

  const latestMessage = messages[messages.length - 1];
  return latestMessage && typeof latestMessage.message === "string"
    ? latestMessage.message
    : "";
}

export function applicationCreationProgressLabel(operation) {
  const message = latestApplicationCreationMessage(operation);
  if (message) {
    return message;
  }

  const progress = operation && operation.progress;
  return typeof progress === "string" && progress !== ""
    ? progress.replace(/_/g, " ")
    : "Waiting for application creation";
}

export function applicationCreationFailureMessage(operation) {
  const displayName =
    operation && typeof operation.displayName === "string" && operation.displayName !== ""
      ? " for " + operation.displayName
      : "";

  return (
    "CASS could not complete application creation" +
    displayName +
    ". API CoE support may need to review it before another application can be created."
  );
}

export function applicationCreationUncertainMessage() {
  return "Your connection ended before CASS confirmed whether application creation started. Please do not submit the same request again immediately. Check your applications in a few minutes or contact API CoE support with the reference below.";
}

export function applicationCreationResultFromError(error) {
  const response = error && error.response;
  const payload = (response && response.data) || {};
  const code = payload.errorCode;
  let title;
  let message;

  if (code === "APPLICATION_CREATION_QUOTA_EXCEEDED") {
    title = "Application limit reached";
    message = "You cannot create another CASS application because you have reached your application limit. Remove an application you no longer need or contact API CoE support.";
  } else if (code === "SAP_DEVELOPER_REGISTRATION_REQUIRED") {
    title = "Registration required";
    message = "Your developer account is not registered for the selected environment. Complete API portal registration or contact API CoE support.";
  } else if (code === "MAINTENANCE_MODE_ACTIVE") {
    title = "CASS is under maintenance";
    message = "CASS is undergoing maintenance. Please try again later.";
  } else if (response && response.status === 409) {
    title = payload.status && payload.status.toLowerCase() === "failed"
      ? "Application creation needs support"
      : "Application creation is already in progress";
    message = applicationCreationConflictMessage(payload);
  } else if (response) {
    title = "CASS could not start application creation";
    message = "Please try again later. If the problem continues, contact API CoE support with the reference below.";
  } else {
    title = "We could not confirm your request";
    message = applicationCreationUncertainMessage();
  }

  return {
    title,
    message,
    publicMessage: typeof payload.message === "string" ? payload.message : "",
    errorCode: code,
    status: response && response.status,
    requestId: payload.requestId,
    operationId: payload.operationId,
  };
}

export function applicationCreationResultFromOperation(operation) {
  return {
    title: "Application creation needs support",
    message: applicationCreationFailureMessage(operation),
    publicMessage:
      operation && operation.failure && typeof operation.failure.message === "string"
        ? operation.failure.message
        : "",
    operationId: operation && operation.operationId,
  };
}

export function applicationSubscriptionFailureResult(error, applicationId) {
  const response = error && error.response;
  const payload = (response && response.data) || {};
  return {
    title: "Application created, subscription could not be completed",
    message: "Your application was created, but the product subscription could not be completed. You can open the application and try the subscription again.",
    publicMessage: typeof payload.message === "string" ? payload.message : "",
    errorCode: payload.errorCode,
    status: response && response.status,
    requestId: payload.requestId,
    applicationId,
  };
}

export function applicationCreationConflictMessage(conflict) {
  const displayName =
    conflict && typeof conflict.displayName === "string" && conflict.displayName !== ""
      ? conflict.displayName
      : "this application";
  const status =
    conflict && typeof conflict.status === "string" && conflict.status !== ""
      ? conflict.status
      : "in progress";
  const createdAt = conflict && typeof conflict.createdAt === "string" ? conflict.createdAt : "";
  const createdAtText = createdAt ? " It was accepted at " + createdAt + "." : "";

  if (status.toLowerCase() === "failed") {
    return (
      "Application creation for " +
      displayName +
      " has failed." +
      createdAtText +
      " API CoE support is investigating and creation is currently blocked."
    );
  }

  return "Application creation for " + displayName + " is " + status + "." + createdAtText + " Please wait for it to finish.";
}

export function waitForApplicationCreationOperation({
  operationId,
  getOperation,
  onUpdate,
  isCancelled,
  sleep,
  pollIntervalMs = 1000,
}) {
  return (async () => {
    while (!isCancelled()) {
      const response = await getOperation(operationId);
      if (isCancelled()) {
        return null;
      }

      const operation = response.data;
      onUpdate(operation);
      if (isTerminalApplicationCreationOperation(operation)) {
        return operation;
      }

      await sleep(pollIntervalMs);
    }

    return null;
  })();
}
